// areas/settings/survey-questions/components/SurveyQuestionEditorOutcomes.tsx
import React, { useMemo, useState } from 'react';
import { Button, Form, Message, Modal } from 'semantic-ui-react';
import { Table, HeaderCell, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { OutcomesGroup, OutcomesQuestion } from '../constants/SurveyQuestionTypes';
import SurveyQuestionOutcomeModal from './SurveyQuestionOutcomeModal';
import { PageSection, Subtitle } from 'common/styles/page';
import { Card } from 'common/components/Card';

/** Confirm Modal */
const ConfirmModal: React.FC<{
  open: boolean;
  title: string;
  content: React.ReactNode;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ open, title, content, confirmLabel = 'Delete', onConfirm, onCancel }) => (
  <Modal open={open} onClose={onCancel} size="small">
    <Modal.Header>{title}</Modal.Header>
    <Modal.Content>{typeof content === 'string' ? <p>{content}</p> : content}</Modal.Content>
    <Modal.Actions>
      <Button onClick={onCancel}>Cancel</Button>
      <Button negative icon="trash" content={confirmLabel} onClick={onConfirm} />
    </Modal.Actions>
  </Modal>
);

/** Section Create Modal */
const SectionModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (v: { id: string; name: string; description?: string }) => void;
  suggestedId: string;
  existingIds: Set<string>;
}> = ({ open, onClose, onSave, suggestedId, existingIds }) => {
  const [id, setId] = useState(suggestedId);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  React.useEffect(() => {
    if (open) {
      setId(suggestedId);
      setName('');
      setDescription('');
    }
  }, [open, suggestedId]);

  const idTaken = existingIds.has(id.trim());
  const canSave = id.trim() && name.trim() && !idTaken;

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header>Add Question Group</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Group widths="equal">
            <Form.Input
              label="Group ID"
              value={id}
              onChange={(_, { value }) => setId(String(value))}
              error={idTaken ? { content: 'ID already exists', pointing: 'below' } : null}
            />
            <Form.Input
              label="Name"
              value={name}
              onChange={(_, { value }) => setName(String(value))}
            />
          </Form.Group>
          <Form.TextArea
            label="Description (optional)"
            value={description}
            onChange={(_, { value }) => setDescription(String(value))}
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          primary
          disabled={!canSave}
          onClick={() => {
            onSave({ id: id.trim(), name: name.trim(), description: description.trim() || undefined });
            onClose();
          }}
        >
          Save
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

/** Section Edit Modal (name/description only) */
const GroupEditModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (v: { name: string; description?: string }) => void;
  initial?: { name?: string; description?: string };
}> = ({ open, onClose, onSave, initial }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  React.useEffect(() => {
    if (open) {
      setName(initial?.name ?? '');
      setDescription(initial?.description ?? '');
    }
  }, [open, initial]);

  const canSave = name.trim().length > 0;

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header>Edit Question Group</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Input
            label="Group Name"
            value={name}
            onChange={(_, { value }) => setName(String(value))}
          />
          <Form.TextArea
            label="Description (optional)"
            value={description}
            onChange={(_, { value }) => setDescription(String(value))}
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          primary
          disabled={!canSave}
          onClick={() => {
            onSave({ name: name.trim(), description: description.trim() || undefined });
            onClose();
          }}
        >
          Save
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

/** Main Editor */
type Props = {
  groups: OutcomesGroup[] | null | undefined;
  onChange: (next: OutcomesGroup[]) => void;
  /** when false => read-only (archived); when true => editable (active) */
  isEditable?: boolean;
};

const SurveyQuestionEditorOutcomes: React.FC<Props> = ({ groups, onChange, isEditable = false }) => {
  const list = groups ?? [];
  const canEdit = !!isEditable;

  const [editOpen, setEditOpen] = useState(false);
  const [ctx, setCtx] = useState<{ groupId: OutcomesGroup['id']; q: OutcomesQuestion } | null>(null);

  const [sectionOpen, setSectionOpen] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createTargetGroupId, setCreateTargetGroupId] = useState<OutcomesGroup['id'] | null>(null);

  const [delQOpen, setDelQOpen] = useState(false);
  const [delQCtx, setDelQCtx] = useState<{ groupId: OutcomesGroup['id']; q: OutcomesQuestion } | null>(null);

  const [delSectionOpen, setDelSectionOpen] = useState(false);
  const [delSectionId, setDelSectionId] = useState<OutcomesGroup['id'] | null>(null);

  const [editSectionOpen, setEditSectionOpen] = useState(false);
  const [editSection, setEditSection] = useState<OutcomesGroup | null>(null);

  const existingGroupIdSet = useMemo(() => new Set(list.map(g => String(g.id))), [list]);

  const suggestedSectionId = useMemo(() => {
    const nums = list.map(g => parseInt(String(g.id), 10)).filter(n => !Number.isNaN(n));
    const next = nums.length ? Math.max(...nums) + 1 : 1;
    return String(next);
  }, [list]);

  const computeNewQuestionFields = (group: OutcomesGroup) => {
    const nextS = (group.questions?.reduce((m, q) => Math.max(m, Number(q.s) || 0), 0) || 0) + 1;
    const groupNumFromId = parseInt(String(group.id), 10);
    const qVal = Number.isFinite(groupNumFromId)
      ? groupNumFromId
      : Math.max(1, list.findIndex(g => g.id === group.id) + 1);
    return { q: qVal, s: nextS, id: String(nextS) };
  };

  const handleRowClick = (groupId: OutcomesGroup['id'], q: OutcomesQuestion) => {
    if (!canEdit) return; // read-only
    setCtx({ groupId, q });
    setEditOpen(true);
  };
  const handleEditClose = () => { setEditOpen(false); setCtx(null); };
  const handleEditSave = ({ title, scale }: { title: string; scale: string[] }) => {
    if (!groups || !ctx) return;
    const next = list.map(g =>
      g.id === ctx.groupId
        ? { ...g, questions: g.questions.map(x => (x.id === ctx.q.id ? { ...x, question: title, scale } : x)) }
        : g
    );
    onChange(next);
  };

  const handleCreateSection = (v: { id: string; name: string; description?: string }) => {
    if (!canEdit) return;
    const newGroup: OutcomesGroup = { id: v.id, name: v.name, description: v.description, questions: [] };
    onChange([...list, newGroup]);
  };

  const openCreateQuestion = (groupId: OutcomesGroup['id']) => {
    if (!canEdit) return;
    setCreateTargetGroupId(groupId);
    setCreateOpen(true);
  };
  const handleCreateClose = () => { setCreateOpen(false); setCreateTargetGroupId(null); };
  const handleCreateSave = ({ title, scale }: { title: string; scale: string[] }) => {
    if (!canEdit || createTargetGroupId == null) return;
    const next = list.map(g => {
      if (g.id !== createTargetGroupId) return g;
      const { q, s, id } = computeNewQuestionFields(g);
      const newQ: OutcomesQuestion = { q, s, id, question: title, scale };
      return { ...g, questions: [...(g.questions ?? []), newQ] };
    });
    onChange(next);
  };

  const openDeleteQuestion = (groupId: OutcomesGroup['id'], q: OutcomesQuestion) => {
    if (!canEdit) return;
    setDelQCtx({ groupId, q });
    setDelQOpen(true);
  };
  const confirmDeleteQuestion = () => {
    if (!canEdit || !delQCtx) return;
    const next = list.map(g =>
      g.id === delQCtx.groupId
        ? { ...g, questions: (g.questions ?? []).filter(x => x.id !== delQCtx.q.id) }
        : g
    );
    onChange(next);
    setDelQOpen(false);
    setDelQCtx(null);
  };

  const openDeleteSection = (groupId: OutcomesGroup['id']) => {
    if (!canEdit) return;
    setDelSectionId(groupId);
    setDelSectionOpen(true);
  };
  const confirmDeleteSection = () => {
    if (!canEdit || delSectionId == null) return;
    const next = list.filter(g => g.id !== delSectionId);
    onChange(next);
    setDelSectionOpen(false);
    setDelSectionId(null);
  };

  const openEditSection = (group: OutcomesGroup) => {
    if (!canEdit) return;
    setEditSection(group);
    setEditSectionOpen(true);
  };
  const confirmEditSection = (v: { name: string; description?: string }) => {
    if (!canEdit || !editSection) return;
    const next = list.map(g =>
      g.id === editSection.id ? { ...g, name: v.name, description: v.description } : g
    );
    onChange(next);
    setEditSectionOpen(false);
    setEditSection(null);
  };

  const showActionsCol = canEdit;
  const addRowColSpan = showActionsCol ? 4 : 3;

  return (
    <PageSection padbottom padtop>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Subtitle>Outcome Questions</Subtitle>
        {canEdit && (
          <Button primary icon="plus" content="Add Question Group" onClick={() => setSectionOpen(true)} />
        )}
      </div>

      {!list.length && <Message info content="No outcomes sections yet." />}

      {list.map((g) => (
        <div key={g.id} style={{ marginBottom: 18 }}>
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <div>
                <strong style={{ fontSize: 15 }}>{g.name}</strong>
                {g.description && <span style={{ marginLeft: 8, color: '#6b7280' }}>— {g.description}</span>}
              </div>
              {canEdit && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button basic size="mini" icon="edit" title="Edit Group" onClick={() => openEditSection(g)} />
                  <Button basic size="mini" icon="trash" title="Delete Group" onClick={() => openDeleteSection(g.id)} />
                </div>
              )}
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <HeaderCell width={10}>Question</HeaderCell>
                  <HeaderCell width={4}>Scale</HeaderCell>
                  <HeaderCell width={2}>QID</HeaderCell>
                  {showActionsCol && <HeaderCell width={1} justify="center">Actions</HeaderCell>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(g.questions ?? []).map((q) => (
                  <TableRow
                    key={q.id}
                    $selectable={canEdit as any}
                    onClick={() => handleRowClick(g.id, q)}
                    style={!canEdit ? { cursor: 'default' } : undefined}
                  >
                    <TableCell $firstcol>{q.question}</TableCell>
                    <TableCell>{q.scale?.join(' — ') ?? '—'}</TableCell>
                    <TableCell>{q.id}</TableCell>
                    {showActionsCol && (
                      <TableCell justify="center" onClick={(e: any) => e.stopPropagation()}>
                        <Button
                          basic
                          size="mini"
                          icon="trash"
                          title="Delete Question"
                          onClick={() => openDeleteQuestion(g.id, q)}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                ))}

                {/* Add row (only in draft) */}
                {canEdit && (
                  <TableRow>
                    <TableCell colSpan={addRowColSpan} style={{ padding: '12px 8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button basic color="green" icon="plus" content="Add Question" onClick={() => openCreateQuestion(g.id)} />
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </div>
      ))}

      {/* Draft-only modals */}
      {canEdit && ctx && (
        <SurveyQuestionOutcomeModal
          open={editOpen}
          onClose={handleEditClose}
          onSave={handleEditSave}
          initial={{ title: ctx.q.question || '', scale: ctx.q.scale }}
          isEditing
        />
      )}
      {canEdit && createTargetGroupId != null && (
        <SurveyQuestionOutcomeModal
          open={createOpen}
          onClose={handleCreateClose}
          onSave={handleCreateSave}
          initial={{ title: '', scale: [] }}
        />
      )}
      {canEdit && (
        <SectionModal
          open={sectionOpen}
          onClose={() => setSectionOpen(false)}
          onSave={handleCreateSection}
          suggestedId={suggestedSectionId}
          existingIds={existingGroupIdSet}
        />
      )}
      {canEdit && editSection && (
        <GroupEditModal
          open={editSectionOpen}
          onClose={() => { setEditSectionOpen(false); setEditSection(null); }}
          onSave={confirmEditSection}
          initial={{ name: editSection.name, description: editSection.description }}
        />
      )}
      {canEdit && delQCtx && (
        <ConfirmModal
          open={delQOpen}
          onCancel={() => { setDelQOpen(false); setDelQCtx(null); }}
          onConfirm={confirmDeleteQuestion}
          title="Delete Question"
          content={
            <>
              <p>Are you sure you want to delete this question?</p>
              <p style={{ color: '#6b7280' }}>{delQCtx.q.question}</p>
            </>
          }
          confirmLabel="Delete Question"
        />
      )}
      {canEdit && delSectionId != null && (
        <ConfirmModal
          open={delSectionOpen}
          onCancel={() => { setDelSectionOpen(false); setDelSectionId(null); }}
          onConfirm={confirmDeleteSection}
          title="Delete Question Group"
          content="This will remove the group and all questions it contains. This action cannot be undone."
          confirmLabel="Delete Group"
        />
      )}
    </PageSection>
  );
};

export default SurveyQuestionEditorOutcomes;
